import React from 'react';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import { MultiSelect } from './ui/MultiSelect';
import { Button } from './ui/Button';
import { Trash2 } from 'lucide-react';
export interface FeeSlab {
  id: string;
  feeType: 'Percentage' | 'Fixed' | 'Fixed + Percentage';
  feeValue: string;
  fixedAmount?: string;
  slabLow: string;
  slabHigh: string;
  minCap: string;
  maxCap: string;
  glAccount: string;
  taxProfiles: string[];
  frequency: string;
}
interface FeeSlabRowProps {
  slab: FeeSlab;
  index: number;
  onChange: (id: string, updates: Partial<FeeSlab>) => void;
  onRemove: (id: string) => void;
}
export function FeeSlabRow({
  slab,
  index,
  onChange,
  onRemove
}: FeeSlabRowProps) {
  return <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 relative animate-in fade-in slide-in-from-bottom-2 duration-300" data-id="element-243">
      <div className="absolute -top-3 left-4 bg-gray-100 px-2 py-0.5 rounded text-xs font-medium text-gray-500 border border-gray-200" data-id="element-244">
        Slab {index + 1}
      </div>

      <div className="space-y-4" data-id="element-245">
        {/* Row 1: Fee Type, Value, and Slab Range */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4" data-id="element-246">
          <div className="md:col-span-3" data-id="element-247">
            <Select label="Fee Type" value={slab.feeType} onChange={e => onChange(slab.id, {
            feeType: e.target.value as any
          })} options={[{
            value: 'Percentage',
            label: 'Percentage'
          }, {
            value: 'Fixed',
            label: 'Fixed Amount'
          }, {
            value: 'Fixed + Percentage',
            label: 'Fixed + Percentage'
          }]} data-id="element-248" />
          </div>

          <div className="md:col-span-2" data-id="element-249">
            <Input label={slab.feeType === 'Fixed' ? 'Amount' : 'Percentage (%)'} type="number" value={slab.feeValue} onChange={e => onChange(slab.id, {
            feeValue: e.target.value
          })} placeholder={slab.feeType === 'Fixed' ? '500' : '2.5'} data-id="element-250" />
          </div>

          {slab.feeType === 'Fixed + Percentage' && <div className="md:col-span-2" data-id="element-251">
              <Input label="Fixed Amount" type="number" value={slab.fixedAmount || ''} onChange={e => onChange(slab.id, {
            fixedAmount: e.target.value
          })} placeholder="500" data-id="element-252" />
            </div>}

          <div className={slab.feeType === 'Fixed + Percentage' ? 'md:col-span-2' : 'md:col-span-3'} data-id="element-253">
            <Input label="Slab Low" type="number" value={slab.slabLow} onChange={e => onChange(slab.id, {
            slabLow: e.target.value
          })} placeholder="0" data-id="element-254" />
          </div>

          <div className={slab.feeType === 'Fixed + Percentage' ? 'md:col-span-2' : 'md:col-span-3'} data-id="element-255">
            <Input label="Slab High" type="number" value={slab.slabHigh} onChange={e => onChange(slab.id, {
            slabHigh: e.target.value
          })} placeholder="100000" data-id="element-256" />
          </div>

          <div className="md:col-span-1 flex items-end justify-end" data-id="element-257">
            <Button variant="ghost" size="sm" onClick={() => onRemove(slab.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" title="Remove Slab" data-id="element-258">
              <Trash2 className="h-4 w-4" data-id="element-259" />
            </Button>
          </div>
        </div>

        {/* Row 2: Frequency, Caps and GL Account */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4" data-id="element-260">
          <div className="md:col-span-2" data-id="element-261">
            <Select label="Frequency" value={slab.frequency || 'monthly'} onChange={e => onChange(slab.id, {
            frequency: e.target.value
          })} options={[{
            value: 'daily',
            label: 'Daily'
          }, {
            value: 'weekly',
            label: 'Weekly'
          }, {
            value: 'monthly',
            label: 'Monthly'
          }, {
            value: 'yearly',
            label: 'Yearly'
          }]} data-id="element-262" />
          </div>

          <div className="md:col-span-2" data-id="element-263">
            <Input label="Minimum Cap" type="number" value={slab.minCap} onChange={e => onChange(slab.id, {
            minCap: e.target.value
          })} placeholder="Optional" data-id="element-264" />
          </div>

          <div className="md:col-span-2" data-id="element-265">
            <Input label="Maximum Cap" type="number" value={slab.maxCap} onChange={e => onChange(slab.id, {
            maxCap: e.target.value
          })} placeholder="Optional" data-id="element-266" />
          </div>

          <div className="md:col-span-3" data-id="element-267">
            <Select label="GL Account" value={slab.glAccount} onChange={e => onChange(slab.id, {
            glAccount: e.target.value
          })} options={[{
            value: '4100',
            label: '4100 - Fee Income'
          }, {
            value: '4200',
            label: '4200 - Processing Fee Income'
          }, {
            value: '4300',
            label: '4300 - Service Charges'
          }]} data-id="element-268" />
          </div>

          <div className="md:col-span-3" data-id="element-269">
            <MultiSelect label="Tax Profiles (Optional)" value={slab.taxProfiles} onChange={value => onChange(slab.id, {
            taxProfiles: value
          })} options={[{
            value: 'TOF-001',
            label: 'Service Tax on Processing Fee'
          }, {
            value: 'TOF-002',
            label: 'FED on Late Payment Fee'
          }, {
            value: 'TOF-003',
            label: 'VAT on Documentation Fee'
          }, {
            value: 'TOF-004',
            label: 'Withholding Tax on Interest'
          }]} placeholder="Select tax profiles..." data-id="element-270" />
          </div>
        </div>
      </div>
    </div>;
}